import axios from "axios";
import { deviceType } from "react-device-detect";
import { encryptionAuth } from "./authUtil";
import Swal from 'sweetalert2';

/**
 * Checks if the current URL params indicate a Telegram login attempt
 * @param {URLSearchParams} urlParams - URL search parameters
 * @returns {boolean} - True if all required Telegram OAuth params are present
 */
export const isTelegramLogin = (urlParams) => {
  if (!urlParams) {
    return false;
  }

  return urlParams.has('id') &&
    urlParams.has('first_name') &&
    urlParams.has('auth_date') &&
    urlParams.has('hash');
};

/**
 * Handles Telegram login by preparing, encrypting, and submitting the login data
 * @param {URLSearchParams} urlParams - URL search parameters containing Telegram OAuth data
 * @param {Function} postLoginAction - Redux action to call with encrypted data
 * @returns {Promise<void>}
 */
export const handleTelegramLogin = async (urlParams, postLoginAction) => {
  try {

    Swal.fire({
      title: 'Logging in...',
      text: 'Please wait...',
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading(); // Display the loading spinner
      }
    });

    // Convert URLSearchParams to object
    const params = {};
    urlParams.forEach((value, key) => {
      params[key] = value;
    });

    // Store telegram user info in sessionStorage for later use
    sessionStorage.setItem("telegram_user_info", JSON.stringify(params));

    // Transform params for API
    params.telegram_id = params.id;
    if (params.username) {
      params.telegram_username = params.username;
    }
    params.device_type = deviceType;

    // IMPORTANT: Remove username field to avoid triggering username/password authentication flow
    delete params.username;

    // Fetch IP addresses (IPv4 and IPv6) in parallel
    const ipv4Promise = axios.get('https://api4.my-ip.io/v2/ip.json')
      .then((response) => {
        if (response.data) {
          params.ipv4 = response.data.ip;
        }
      })
      .catch((error) => {
        console.warn('Failed to fetch IPv4:', error);
      });

    const ipv6Promise = axios.get('https://api6.my-ip.io/v2/ip.json')
      .then((response) => {
        if (response.data) {
          params.ipv6 = response.data.ip;
        }
      })
      .catch((error) => {
        console.warn('Failed to fetch IPv6:', error);
      });

    // Wait for both IP fetches to complete
    await Promise.all([ipv4Promise, ipv6Promise]);

    // Encrypt the data with trxTime and trxHash
    const encryptedData = encryptionAuth(params);

    // Call the postLogin action with encrypted data
    postLoginAction(encryptedData);
  } catch (error) {
    console.error('Error handling Telegram login:', error);
    throw error;
  }
};
